import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { logos } from '../sections/renewalAssets';
import { navMenu } from '../sections/renewalData';
import { token } from '../sections/renewalStyles';

const HeaderWrap = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 60;
  background: ${({ $onHero }) => ($onHero ? 'rgba(5,8,19,0.55)' : 'rgba(255,255,255,0.92)')};
  backdrop-filter: blur(20px);
  border-bottom: 1px solid ${({ $onHero }) => ($onHero ? 'rgba(255,255,255,0.12)' : token.borderLight)};
  transition: background 0.35s, border-color 0.35s;
`;

const Nav = styled.nav`
  max-width: ${token.maxWidth};
  margin: 0 auto;
  padding: 14px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;

  @media screen and (max-width: 860px) {
    padding: 12px 20px;
    gap: 10px 12px;
    flex-wrap: wrap;
  }
`;

const LogoLink = styled.a`
  display: flex;
  align-items: center;
  flex-shrink: 0;

  img {
    height: 36px;
    width: auto;
    display: block;
    transition: filter 0.35s;
    filter: ${({ $onHero }) => ($onHero ? 'invert(1) brightness(2)' : 'none')};
  }

  @media screen and (max-width: 768px) {
    img {
      height: 28px;
    }
  }
`;

const Menu = styled.div`
  display: flex;
  align-items: center;
  gap: 38px;

  @media screen and (max-width: 1100px) {
    gap: 20px;
  }

  /* 좁은 화면에서는 메뉴를 감추지 않고 두 번째 줄에 가로 스크롤로 배치합니다. */
  @media screen and (max-width: 860px) {
    order: 3;
    width: 100%;
    /* min-width:0 이 없으면 flex 아이템이 링크 전체 너비만큼 벌어져 가로 스크롤이 생깁니다. */
    min-width: 0;
    gap: 18px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const MenuLink = styled.a`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.14em;
  padding: 8px 0;
  white-space: nowrap;
  color: ${({ $active, $onHero }) => ($active ? token.primary : $onHero ? '#E8ECF7' : '#1a1a1a')};
  transition: color 0.35s;

  &:hover {
    color: ${token.primary};
    text-decoration: none;
  }
`;

const Cta = styled.a`
  flex-shrink: 0;
  background: ${({ $onHero }) => ($onHero ? token.primary : '#000')};
  color: #fff;
  padding: 13px 28px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.16em;
  transition: background 0.35s, color 0.35s;

  &:hover {
    color: #fff;
    text-decoration: none;
  }

  @media screen and (max-width: 768px) {
    padding: 11px 16px;
    font-size: 11px;
    letter-spacing: 0.1em;
  }
`;

/**
 * 헤더 상태(히어로 위 / 본문)와 스크롤 스파이.
 * 스크롤 이벤트는 requestAnimationFrame 으로 스로틀하고,
 * 섹션 offsetTop 은 캐시해 두어 매 프레임 강제 리플로우가 나지 않도록 합니다.
 */
export default function Header() {
  const [active, setActive] = useState('');
  const [onHero, setOnHero] = useState(true);
  const metrics = useRef({ offsets: [], heroHeight: 0 });

  useEffect(() => {
    let raf = null;

    const measure = () => {
      const hero = document.getElementById('hero');
      metrics.current = {
        heroHeight: hero ? hero.offsetHeight : 0,
        offsets: navMenu
          .map(({ id }) => {
            const el = document.getElementById(id);
            return el ? { id, top: el.offsetTop } : null;
          })
          .filter(Boolean),
      };
    };

    const update = () => {
      raf = null;
      const { offsets, heroHeight } = metrics.current;
      const y = window.scrollY + 200;

      let current = '';
      offsets.forEach(section => {
        if (y >= section.top) current = section.id;
      });

      setActive(current);
      setOnHero(heroHeight > 0 && window.scrollY < heroHeight - 90);
    };

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(update);
    };

    const onResize = () => {
      measure();
      onScroll();
    };

    measure();
    update();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    // 이미지·폰트가 늦게 로드되면 섹션 위치가 바뀌므로 한 번 더 잽니다.
    window.addEventListener('load', onResize);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('load', onResize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <HeaderWrap $onHero={onHero}>
      <Nav>
        <LogoLink href="#hero" aria-label="BRIT 홈" $onHero={onHero}>
          <img src={logos.header} alt="BRIT" width="112" height="36" />
        </LogoLink>
        <Menu>
          {navMenu.map(item => (
            <MenuLink
              key={item.id}
              href={`#${item.id}`}
              $active={active === item.id}
              $onHero={onHero}
              aria-current={active === item.id ? 'true' : undefined}
            >
              {item.label}
            </MenuLink>
          ))}
        </Menu>
        <Cta href="#contact" $onHero={onHero}>
          CONTACT US
        </Cta>
      </Nav>
    </HeaderWrap>
  );
}
